/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';

import Icon from 'carbon-react/lib/components/icon';

import { useOnboardingContext } from '../../../onboarding.context';
import { useReportBuilderContext } from '../../../report-builder.context';
import { OnboardingArrows } from '../../onboarding/onboarding-arrows/onboarding-arrows';
import { SIDEBAR_ITEM_TYPES, SidebarItemData } from './types';
import { ONBOARDING_STEPS } from '../../../types';
import { usePredictionsInfoContext } from '../../../../forecast/detail/context/predictions-info-context';

import styles from './sidebar-menu.scss';

export interface SidebarMenuProps {
  activeItem?: SIDEBAR_ITEM_TYPES;
  isOpen?: boolean;
  onItemClick?: (item: SIDEBAR_ITEM_TYPES) => void;
  onToggleSidebar?: (e: React.MouseEvent<HTMLElement>, isOpen: boolean) => void;
}

export const SidebarMenu = ({ activeItem, isOpen, onItemClick, onToggleSidebar }: SidebarMenuProps) => {
  const intl = useIntl();
  const {
    isOnboarding,
    isTemplate,
    isViewPage,
    isWorkspaceTemplate,
    isMasterPage,
    reportData
  } = useReportBuilderContext();
  const { isConsolidation } = usePredictionsInfoContext();
  const { currentStep } = useOnboardingContext();

  const items: Record<SIDEBAR_ITEM_TYPES, SidebarItemData> = {
    [SIDEBAR_ITEM_TYPES.BUILDER]: {
      type: SIDEBAR_ITEM_TYPES.BUILDER,
      name: intl.formatMessage({ id: 'reporting.builder', defaultMessage: 'Builder' }),
      icon: (
        <div className={clsx(styles.carbonIconsContainer)}>
          <Icon type="chart_bar" mr="4px" fontSize="small" />
          <Icon type="chart_line" fontSize="small" />
        </div>
      )
    },
    [SIDEBAR_ITEM_TYPES.IMAGES]: {
      type: SIDEBAR_ITEM_TYPES.IMAGES,
      name: intl.formatMessage({ id: 'reporting.images', defaultMessage: 'Images' }),
      icon: <Icon type="image" mb={1} fontSize="small" />
    },
    // [SIDEBAR_ITEM_TYPES.OTHER]: {
    //   type: SIDEBAR_ITEM_TYPES.OTHER,
    //   name: intl.formatMessage({ id: 'predict.report_builder.sidebar.more', defaultMessage: 'More' }),
    //   icon: <UIMoreHorizontal />
    // }
    [SIDEBAR_ITEM_TYPES.SCHEDULE]: {
      type: SIDEBAR_ITEM_TYPES.SCHEDULE,
      name: intl.formatMessage({ id: 'reporting.schedule', defaultMessage: 'Schedule' }),
      icon: <Icon type="email" mb={1} fontSize="small" />
    },
    [SIDEBAR_ITEM_TYPES.TAGS]: {
      type: SIDEBAR_ITEM_TYPES.TAGS,
      name: intl.formatMessage({ id: 'reporting.tags', defaultMessage: 'Tags' }),
      icon: <Icon type="tag" mb={1} fontSize="small" />
    },
    [SIDEBAR_ITEM_TYPES.TEMPLATES]: {
      type: SIDEBAR_ITEM_TYPES.TEMPLATES,
      name: intl.formatMessage({ id: 'reporting.templates', defaultMessage: 'Templates' }),
      icon: <Icon type="pdf" mb={1} fontSize="small" />
    },
    [SIDEBAR_ITEM_TYPES.TEXT]: {
      type: SIDEBAR_ITEM_TYPES.TEXT,
      name: intl.formatMessage({ id: 'reporting.text', defaultMessage: 'Text' }),
      icon: <Icon type="file_word" mb={1} fontSize="small" />
    },
    [SIDEBAR_ITEM_TYPES.WIDGETS]: {
      type: SIDEBAR_ITEM_TYPES.WIDGETS,
      name: intl.formatMessage({ id: 'reporting.widgets', defaultMessage: 'Widgets' }),
      icon: (
        <div className={clsx(styles.carbonIconsContainer)}>
          <Icon type="chart_pie" mr="4px" fontSize="small" />
          <Icon type="csv" fontSize="small" />
        </div>
      )
    }
  };

  const getEditModeSidebarItems = () => {
    if (isMasterPage) {
      return [items[SIDEBAR_ITEM_TYPES.TEXT], items[SIDEBAR_ITEM_TYPES.IMAGES]];
    }
    let sidebarItems: SidebarItemData[] = [];
    if (isConsolidation) {
      sidebarItems = [
        items[SIDEBAR_ITEM_TYPES.TEMPLATES],
        items[SIDEBAR_ITEM_TYPES.BUILDER],
        items[SIDEBAR_ITEM_TYPES.TEXT],
        items[SIDEBAR_ITEM_TYPES.IMAGES]
      ];
    } else {
      sidebarItems = [
        items[SIDEBAR_ITEM_TYPES.TEMPLATES],
        items[SIDEBAR_ITEM_TYPES.WIDGETS],
        items[SIDEBAR_ITEM_TYPES.BUILDER],
        items[SIDEBAR_ITEM_TYPES.TEXT],
        items[SIDEBAR_ITEM_TYPES.IMAGES]
      ];
    }

    if (!isWorkspaceTemplate && !isTemplate && reportData?.pageSetup?.orientation !== 'dashboard') {
      sidebarItems.push(items[SIDEBAR_ITEM_TYPES.SCHEDULE]);
    }
    if (isTemplate && !isWorkspaceTemplate) {
      sidebarItems.push(items[SIDEBAR_ITEM_TYPES.TAGS]);
    }

    return sidebarItems;
  };

  const getOnboardingModeSidebarItems = () => {
    const sidebarItems: SidebarItemData[] = [items[SIDEBAR_ITEM_TYPES.TEMPLATES]];

    if (currentStep >= ONBOARDING_STEPS.ADD_WIDGET) {
      sidebarItems.push(items[SIDEBAR_ITEM_TYPES.WIDGETS]);
    }

    if (currentStep >= ONBOARDING_STEPS.SELECT_WIDGET) {
      sidebarItems.push(items[SIDEBAR_ITEM_TYPES.BUILDER]);
    }

    if (currentStep >= ONBOARDING_STEPS.ADD_TEXT_IMAGES) {
      sidebarItems.push(items[SIDEBAR_ITEM_TYPES.TEXT]);
      sidebarItems.push(items[SIDEBAR_ITEM_TYPES.IMAGES]);
    }

    if (currentStep >= ONBOARDING_STEPS.SEND_OR_SCHEDULE) {
      sidebarItems.push(items[SIDEBAR_ITEM_TYPES.SCHEDULE]);
    }

    return sidebarItems;
  };

  const getViewModeSidebarItems = () => {
    const sidebarItems: SidebarItemData[] = [];

    if (!isWorkspaceTemplate && !isTemplate) {
      sidebarItems.push(items[SIDEBAR_ITEM_TYPES.SCHEDULE]);
    }

    return sidebarItems;
  };

  const getSidebarItems = () => {
    if (isViewPage) {
      return getViewModeSidebarItems();
    } else if (isOnboarding) {
      return getOnboardingModeSidebarItems();
    } else {
      return getEditModeSidebarItems();
    }
  };

  const isHighlightedForOnboarding = (item: SidebarItemData) => {
    return (
      (currentStep === ONBOARDING_STEPS.ADD_TEXT_IMAGES && item.type === SIDEBAR_ITEM_TYPES.TEXT) ||
      (currentStep === ONBOARDING_STEPS.ADD_TEXT_IMAGES && item.type === SIDEBAR_ITEM_TYPES.IMAGES)
    );
  };

  return (
    <div
      className={clsx(styles.sidebarMenu, isTemplate && styles.templateColor)}
      onClick={e => onToggleSidebar?.(e, true)}
    >
      {getSidebarItems().map(elmt => {
        return (
          <div
            key={elmt.name}
            className={clsx([
              styles.sidebarMenuItem,
              {
                [styles.sidebarMenuItemActive]: isOpen && (activeItem === elmt.type || isHighlightedForOnboarding(elmt))
              }
            ])}
            onClick={() => onItemClick?.(elmt.type)}
          >
            <span>{elmt.icon}</span>
            <span data-testid="SidebarMenu-itemName">{elmt.name}</span>
          </div>
        );
      })}
      {isOpen && !isOnboarding && (
        <div className={clsx(styles.hideSidebarContent)} onClick={e => onToggleSidebar?.(e, false)}>
          <Icon type="caret_right" color="#fff" className={clsx(styles.carbonArrowIcon)} />
        </div>
      )}
      {isOnboarding && <OnboardingArrows position="sidebarMenu" />}
    </div>
  );
};
